import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  styled,
  ButtonGroup,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Print as PrintIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Quote, QuoteStatus } from '../../types/quote.types';
import { PropertyAddress } from '../../types/property.types';

// Styled component for clickable row
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& td': {
    borderColor: theme.palette.divider,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
}));

// Remove the styled header cell as we'll use Typography instead

interface QuoteListProps {
  quotes?: Quote[];
  clientId?: string;
  propertyId?: string;
  onViewQuote: (quoteId: string) => void;
  onPrintQuote?: (quoteId: string) => void;
  onSendQuote?: (quoteId: string) => void;
  onDeleteQuote?: (quoteId: string) => void;
  onAcceptQuote?: (quoteId: string) => void;
  onRejectQuote?: (quoteId: string) => void;
  showPropertyName?: boolean;
  showClientName?: boolean;
  loading?: boolean;
}

const QuoteList: React.FC<QuoteListProps> = ({
  quotes = [],
  showClientName = true,
  showPropertyName = true,
  onViewQuote,
  onPrintQuote,
  onSendQuote,
  onDeleteQuote,
  onAcceptQuote,
  onRejectQuote,
  loading = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'ALL'>('ALL');

  const handleRowClick = (quoteId: string) => {
    onViewQuote(quoteId);
  };

  // Filter and sort quotes
  const filteredQuotes = useMemo(() => {
    if (!Array.isArray(quotes)) return [];
    
    return quotes.filter((quote) => {
      // Format property address for search if it exists
      let formattedAddress = '';
      if (quote.property?.address && typeof quote.property.address === 'object') {
        const addr = quote.property.address as PropertyAddress;
        formattedAddress = [
          addr.line1,
          addr.city,
          addr.postcode,
          addr.country
        ].filter(Boolean).join(', ');
      }
      
      const matchesSearch = !searchTerm || [
        quote.quoteRef,
        quote.client?.companyName,
        quote.property?.address && typeof quote.property.address === 'object' ? 
          `${(quote.property.address as PropertyAddress).line1}, ${(quote.property.address as PropertyAddress).city}` : 
          (quote.property?.address as string || ''),
        formattedAddress,
        quote.meterIdentifier
      ].some(value => {
        if (typeof value !== 'string') return false;
        return value.toLowerCase().includes(searchTerm.toLowerCase());
      });
      
      const matchesStatus = statusFilter === 'ALL' || quote.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [quotes, searchTerm, statusFilter]);

  // Calculate paginated data
  const paginatedQuotes = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredQuotes.slice(start, end);
  }, [filteredQuotes, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by client, property, quote reference, or meter ID..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page when searching
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value as QuoteStatus | 'ALL');
              setPage(0); // Reset to first page when filtering
            }}
          >
            <MenuItem value="ALL">All Statuses</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="ISSUED">Issued</MenuItem>
            <MenuItem value="SELECTED">Selected</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              {showClientName && <StyledTableCell><Typography fontWeight="bold">Client</Typography></StyledTableCell>}
              {showPropertyName && <StyledTableCell><Typography fontWeight="bold">Property</Typography></StyledTableCell>}
              <StyledTableCell><Typography fontWeight="bold">Quote Ref</Typography></StyledTableCell>
              <StyledTableCell><Typography fontWeight="bold">Meter Type</Typography></StyledTableCell>
              <StyledTableCell><Typography fontWeight="bold">Meter ID</Typography></StyledTableCell>
              <StyledTableCell><Typography fontWeight="bold">Date</Typography></StyledTableCell>
              <StyledTableCell><Typography fontWeight="bold">Status</Typography></StyledTableCell>
              <StyledTableCell align="right"><Typography fontWeight="bold">Actions</Typography></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <StyledTableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                    <CircularProgress size={24} />
                    <Typography variant="body1">Loading quotes...</Typography>
                  </Box>
                </StyledTableCell>
              </TableRow>
            ) : !Array.isArray(quotes) || quotes.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No quotes found
                  </Typography>
                </StyledTableCell>
              </TableRow>
            ) : (
              paginatedQuotes.map((quote) => (
                <StyledTableRow 
                  key={quote._id} 
                  onClick={() => handleRowClick(quote._id)}
                  hover
                >
                  {showClientName && (
                    <StyledTableCell>
                      <Typography variant="body1">{quote.client?.companyName || 'N/A'}</Typography>
                    </StyledTableCell>
                  )}
                  {showPropertyName && (
                    <StyledTableCell>
                      <Typography variant="body1">
                        {quote.property?.address && typeof quote.property.address === 'object' ? 
                            `${(quote.property.address as PropertyAddress).line1}, ${(quote.property.address as PropertyAddress).city}` : 
                            (quote.property?.address as string || 'N/A')}
                      </Typography>
                    </StyledTableCell>
                  )}
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body1">{quote.quoteRef || 'N/A'}</Typography>
                      {quote.version && quote.version > 1 && (
                        <Chip
                          label={`v${quote.version}`}
                          size="small"
                          color="info"
                          sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1">{quote.meterType || 'N/A'}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1">{quote.meterIdentifier || 'N/A'}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1">
                      {new Date(quote.dateOfQuotation).toLocaleDateString()}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={quote.status}
                      color={
                        quote.status === 'SELECTED' ? 'success' :
                        quote.status === 'COMPLETED' ? 'success' :
                        quote.status === 'REJECTED' ? 'error' :
                        quote.status === 'ISSUED' ? 'primary' : 'default'
                      }
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <ButtonGroup size="small">
                      <Tooltip title="View Quote">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewQuote(quote._id);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {(quote.status === 'DRAFT' || quote.status === 'DOWNLOADED') && onSendQuote && (
                        <Tooltip title="Send Quote">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSendQuote(quote._id);
                            }}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(quote.status === 'ISSUED' || quote.status === 'SELECTED' || quote.status === 'DOWNLOADED') && onAcceptQuote && (
                        <Tooltip title="Accept Quote">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAcceptQuote(quote._id);
                            }}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(quote.status === 'ISSUED' || quote.status === 'DOWNLOADED') && onRejectQuote && (
                        <Tooltip title="Reject Quote">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRejectQuote(quote._id);
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {quote.status !== 'COMPLETED' && onDeleteQuote && (
                        <Tooltip title="Delete Quote">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteQuote(quote._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onPrintQuote && (
                        <Tooltip title="Print Quote">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPrintQuote(quote._id);
                            }}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ButtonGroup>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredQuotes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            '.MuiTablePagination-select': {
              paddingTop: 1,
              paddingBottom: 1,
            },
            '.MuiTablePagination-displayedRows': {
              margin: 0,
            },
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default QuoteList;
